import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { supplierRiskAssessment } from '../../../../lib/supplyChainRisks';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialBadge, EditorialCallout, EditorialDataTable, type EditorialBadgeTone } from '../../report/editorial';
import { resolveSectionCallout } from '../../../../lib/reportRendering/reportSectionCallouts';
import { SectionShell } from '../SectionShell';
import { resolveSupplyChainPlan } from '../../../../lib/reportRendering/supplyChainPlan';

/** Supplier risk tier → EditorialBadge tone (matches the SCM severity map). */
function tierTone(tier: string): EditorialBadgeTone {
  switch (tier) {
    case 'critical':
    case 'high':
      return 'negative';
    case 'medium':
      return 'warning';
    case 'low':
      return 'positive';
    default:
      return 'neutral';
  }
}

/**
 * W2b editorial port — the ad-hoc mono-headed table becomes an
 * EditorialDataTable (mono small-caps header over an ink underline,
 * hairline rows) with the overall tier as an outline EditorialBadge.
 * Inputs unchanged: same supplierRiskAssessment ranking (tier
 * localeCompare, descending), top-15 slice, intro with citation walking,
 * identical hasData gate.
 */
export const SupplierRiskRankingSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const plan = resolveSupplyChainPlan(report);
  const intro = report.supplyChainAnalysis?.supplierStrategy?.trim();
  const ranked = plan
    ? plan.suppliers
        .map((supplier) => ({ supplier, risk: supplierRiskAssessment(supplier) }))
        .sort((a, b) => b.risk.overallTier.localeCompare(a.risk.overallTier))
        .slice(0, 15)
    : [];
  const hasData = ranked.length > 0 || !!intro;

  const callout = resolveSectionCallout(report, 'supplierRiskRanking');

  return (
    <SectionShell label={label} hasData={hasData} report={report} sectionId="supplierRiskRanking" required>
      {intro && (
        <p className="text-sm mb-4" style={{ color: roleColor(theme, 'body') }}>{walkCitationText(intro, report.references)}</p>
      )}
      {callout && (
        <EditorialCallout theme={theme} tone={callout.tone} label={callout.label} style={{ marginTop: 8 }}>
          {callout.body}
        </EditorialCallout>
      )}
      {ranked.length > 0 && (
        /* data-span-columns: defensive — lets the wide ranking table span both
           flow columns if this section's supportsColumnLayout is flipped on.
           Carrier div holds the testid (EditorialDataTable has no testid prop). */
        <div data-span-columns className="overflow-x-auto" data-testid="supplier-risk-ranking-table">
          <EditorialDataTable
            theme={theme}
            columns={[
              { header: 'Supplier' },
              { header: 'Tier', width: 110 },
              { header: 'Geo' },
              { header: 'Financial' },
            ]}
            rows={ranked.map(({ supplier, risk }) => [
              supplier.name,
              <EditorialBadge key="tier" theme={theme} tone={tierTone(risk.overallTier)} variant="outline">
                {risk.overallTier}
              </EditorialBadge>,
              risk.geopoliticalRiskLevel,
              risk.financialHealthTier,
            ])}
          />
        </div>
      )}
    </SectionShell>
  );
};
